import { useState } from 'react'
import { Bookmark, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useInsertProjectReferenceAtRoot,
  useMyProjectsForPicker,
} from '@/features/projects/use-projects'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { ProjectDetail } from '@/api/types'

interface ReferenceThisButtonProps {
  /** The Project being viewed — its UUID becomes the source of the inserted reference. */
  source: ProjectDetail
  /** Handle of the source's author (used for nicer toast copy). */
  sourceAuthorHandle: string
  /** Viewer's own handle — needed to build the master's canonical URL after insert. */
  viewerHandle: string
}

/**
 * Phase 6 viewer-side button: "Reference this in one of my projects". Shown to signed-in
 * non-author viewers on PUBLIC Projects. On click, opens {@code MyProjectsPickerDialog}; on
 * pick, POSTs to the insert-at-root endpoint and redirects to the master's canonical URL.
 *
 * <p>Visibility of the button is the caller's responsibility — we only render the dialog plumbing
 * here; the parent decides whether to show the button at all.
 */
export const ReferenceThisButton = ({
  source,
  sourceAuthorHandle,
  viewerHandle,
}: ReferenceThisButtonProps) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const picker = useMyProjectsForPicker(open)
  const insert = useInsertProjectReferenceAtRoot()

  const handlePick = (master: { id: string; title: string; slug: string }) => {
    insert.mutate(
      { masterProjectId: master.id, sourceProjectId: source.id },
      {
        onSuccess: () => {
          setOpen(false)
          toast.success(
            `Inserted "${source.title}" by @${sourceAuthorHandle} into "${master.title}"`
          )
          // Master is the viewer's own project, so author handle == viewerHandle.
          navigate(canonicalProjectUrl(viewerHandle, master.slug))
        },
        onError: (err) => {
          const apiErr = err as { status?: number; detail?: string }
          if (apiErr.status === 403) {
            toast.error(apiErr.detail ?? "That project isn't publicly referenceable right now")
          } else {
            toast.error(apiErr.detail ?? 'Could not insert Project reference')
          }
        },
      }
    )
  }

  return (
    <>
      <Button variant="outline" size="sm" className="gap-2" onClick={() => setOpen(true)}>
        <Bookmark className="h-4 w-4" />
        Reference this in one of my projects
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reference into one of your projects</DialogTitle>
            <DialogDescription>
              &ldquo;{source.title}&rdquo; will be snapshotted into the project you pick — a
              top-level Project reference, ready to organize.
            </DialogDescription>
          </DialogHeader>

          {picker.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading your projects…
            </div>
          )}
          {picker.isError && (
            <p className="text-sm text-destructive">Could not load your projects. Try again.</p>
          )}
          {picker.data && picker.data.length === 0 && (
            <p className="text-sm text-muted-foreground">
              You don&apos;t have any projects yet. Create one first, then come back.
            </p>
          )}
          {picker.data && picker.data.length > 0 && (
            <ul className="max-h-72 space-y-1 overflow-y-auto">
              {picker.data
                .filter((m) => m.id !== source.id)
                .map((m) => (
                  <li
                    key={m.id}
                    className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{m.title}</p>
                      <p className="truncate font-mono text-xs text-muted-foreground">/{m.slug}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={insert.isPending}
                      onClick={() => handlePick(m)}
                    >
                      {insert.isPending ? 'Inserting…' : 'Insert here'}
                    </Button>
                  </li>
                ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
